import { useMemo } from 'react';
import { todayIso } from '@platform/core/lib/format';
import type { IModuleDefinition, IRecord, IRecordTypeDefinition } from '@platform/core/types';
import { useAreasQuery, useIterationsQuery, useListRecordsQuery, useMeQuery } from '@platform/state/api';
import { draftRecord } from './draftRecord';
import { newRecordDefaults } from './newRecordDefaults';
import { useNextCode } from './useNextCode';

/** Крок відносного пріоритету нового запису (прототип: найбільший ранг + 1000, як RankCalculator бекенду). */
const RANK_STEP = 1000;

/**
 * Основа нової чернетки (прототип v4 createRecord): номер — наступний за найбільшим, виконавець — я, напрям — корінь, ітерація — поточний
 * спринт, відносний пріоритет — найбільший + 1000 (реєстри рангу не мають; не вдалося прочитати — ранг дасть сервер), поверх — наперед
 * задані значення (доріжка дошки, пресет); null, доки не завантажено довідники.
 */
export function useDraftRecord(ws: string, module: IModuleDefinition, rt: IRecordTypeDefinition, preset: Record<string, unknown> | null | undefined): IRecord | null {
  const { data: me } = useMeQuery();
  const needAreas = rt.fields.some((f) => f.type === 'area');
  const needIterations = rt.fields.some((f) => f.type === 'iteration');
  const hasRank = !module.module.registry && rt.fields.some((f) => f.key === 'rank' && f.type === 'number');
  const areas = useAreasQuery(ws, { skip: !needAreas });
  const iterations = useIterationsQuery(ws, { skip: !needIterations });
  const top = useListRecordsQuery({ ws, rt: rt.key, params: { sort: '-rank', pageSize: 10, facets: false } }, { skip: !hasRank });
  const code = useNextCode(ws, rt.key);
  const loading = !me || (needAreas && areas.isLoading) || (needIterations && iterations.isLoading) || (hasRank && top.isLoading) || code === undefined;
  return useMemo(() => {
    if (loading) return null;
    const defaults = newRecordDefaults(rt, me?.id, areas.data ?? [], iterations.data ?? [], todayIso());
    if (hasRank && top.data) defaults['rank'] = Math.max(0, ...top.data.items.map((r) => Number(r.values['rank']) || 0)) + RANK_STEP;
    return draftRecord(rt, module.module.key, { ...defaults, ...(preset ?? {}) }, new Date().toISOString(), code);
  }, [loading, rt, me?.id, areas.data, iterations.data, hasRank, top.data, module.module.key, preset, code]);
}
